import React from "react";

import { message } from "antd";

import { EMPTY_SPACE } from "../../constants";
import { SpaceContext } from "../../SpaceRoot/SpaceContext";
import {
  collectComponents,
  _getBinding
} from "../../SpaceRoot/SpaceContext/SpaceContext";
import { OPAQUE_EDIT_MODE_KEY } from "../../SpaceRoot/SpaceContext/StableSpaceContext";
import { getAncestorComponents as baseGetAncestorComponents } from "../../SpaceRoot/SpaceContext/util";
import { ComponentConfigState } from "../../types";

import useSpaceConfig, { Result as SpaceConfigResult } from "./useSpaceConfig";
import { BaseSpaceConfig, INITIAL_STATE } from "./useSpaceConfig/reducer/reducer";
import { UseConfigMutationData } from "./useSpaceConfig/useConfigMutation/useConfigMutation";
import { DestroyConfigMutationData } from "./useSpaceConfig/useDestroyConfigMutation/useDestroyConfigMutation";

export type SpaceConfigContextType = SpaceConfigResult;

export interface SpaceContextProviderProps {
  slug: string;
  children: React.ReactNode;
  onNewSpaceCreated: (result: UseConfigMutationData) => void;
  onDestroy: (result: DestroyConfigMutationData) => void;
}

export const defaultContextValue: SpaceConfigContextType = {
  [OPAQUE_EDIT_MODE_KEY]: false, // This is set true when there SpaceConfigContextProvider present in tree
  space: EMPTY_SPACE,
  state: INITIAL_STATE,
  loading: true,
  mutationLoading: false,
  shouldDisplayError: (_: string) => false,
  dispatch: () => null,
  destroy: () => null,
  save: () => null,
  predictSlug: () => "",
  getComponentConfig: () => ({} as ComponentConfigState),
  getChildren: () => [],
  getComponentSpaceConfig: () => ({} as BaseSpaceConfig),
  componentsWithErrors: new Set(),
  componentTree: [],
  queryStatus: 0,
  configErrors: {}
};

export const SpaceConfigContext =
  React.createContext<SpaceConfigContextType>(defaultContextValue);

export function SpaceConfigContextProvider({
  slug,
  children,
  onNewSpaceCreated,
  onDestroy
}: SpaceContextProviderProps) {
  const [debugMode, setDebugMode] = React.useState(false);
  const result = useSpaceConfig(onDestroy, onNewSpaceCreated, slug);
  const components = React.useMemo(
    () => collectComponents(result.componentTree),
    [result.componentTree]
  );
  const getAncestorComponents = React.useCallback(
    (slug: string | undefined) => baseGetAncestorComponents(slug, components),
    [components]
  );
  const getBinding = React.useCallback(
    (path: string | null | undefined) =>
      _getBinding(result.componentTree, path, result.state.spaces),
    [result.componentTree, result.state.spaces]
  );
  const toggleDebugMode = React.useCallback(() => {
    message.info(`Debug mode ${debugMode ? "off" : "on"}`);
    setDebugMode(!debugMode);
  }, [debugMode]);

  // Replace components with draft versions
  const spaceContextValue = React.useMemo(() => {
    return {
      slug,
      queryStatus: result.queryStatus,
      space: result.space,
      loading: result.loading,
      editMode: true,
      componentTree: result.componentTree,
      components,
      debugMode,
      contextParams: {}, // TODO: this is currently only set for embedded space context
      getAncestorComponents,
      getBinding,
      toggleDebugMode
    };
  }, [
    slug,
    result,
    components,
    debugMode,
    getAncestorComponents,
    getBinding,
    toggleDebugMode
  ]);

  return (
    <SpaceConfigContext.Provider value={result}>
      <SpaceContext.Provider value={spaceContextValue}>
        {children}
      </SpaceContext.Provider>
    </SpaceConfigContext.Provider>
  );
}

export const useSpaceConfigContext = () => React.useContext(SpaceConfigContext);
